/**
 * Detail page for ONE past LINE campaign send — opened from a Sent-history row
 * on the Campaigns page (previously an inline expand). Shows the full send
 * summary, visit attribution, and the paginated recipient list.
 */

import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Loader2, Send, Users, Eye, ShoppingBag, Ticket } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { PageContainer } from '@/components/layout/PageContainer'
import { getERPClient } from '@/lib/erp/client'
import { usePageTranslation } from '@/i18n/useTranslation'
import { useLanguage } from '@/i18n/useLanguage'
import { lineCampaignTranslations as enT } from '@/i18n/locales/en/lineCampaign'
import { lineCampaignTranslations as thT } from '@/i18n/locales/th/lineCampaign'
import { useLineCampaignHistory, useLineCampaignRecipients, useLineCampaignVisits } from './useLineCampaign'
import { RecipientList, formatSentAt } from './CampaignSentHistory'

/** Pull a coupon code out of the broadcast message ("Code: MISSYOU150", "โค้ด …"). */
function extractCouponCode(text: string | null | undefined): string | null {
  if (!text) return null
  const m = text.match(/(?:code|coupon|โค้ด|คูปอง)\s*[:：]?\s*([A-Z0-9][A-Z0-9._-]{2,})/i)
  return m ? m[1].toUpperCase() : null
}

function pct(part: number, whole: number): string {
  if (!whole) return '—'
  return `${Math.round((part / whole) * 100)}%`
}

/**
 * Performance funnel for one send: Sent → Reachable → Visited → Ordered, plus a
 * reachability bar (reached/blocked) and visit-rate / CVR chips. Honest about
 * unavailable data: no link → visits/orders can't be measured; block-check may
 * be capped. Reuses the cached recipients + visits queries (no extra fetch).
 */
function CampaignFunnel({ logName, sent, hasLink, th }: { logName: string; sent: number; hasLink: boolean; th: boolean }) {
  const { data: rec } = useLineCampaignRecipients(logName, true)
  const { data: visits } = useLineCampaignVisits(logName, hasLink)

  const blocked = rec ? rec.recipients.filter((r) => r.blocked === true).length : 0
  const reachable = Math.max(0, sent - blocked)
  const visitsAvail = !!visits && visits.configured && visits.unique_visitors >= 0
  const visited = visitsAvail ? visits!.unique_visitors : null
  const ordered = visits && visits.orders >= 0 ? visits.orders : null

  const steps: { icon: typeof Send; label: string; value: number | null; of: number | null }[] = [
    { icon: Send, label: th ? 'ส่งแล้ว' : 'Sent', value: sent, of: null },
    { icon: Users, label: th ? 'ติดต่อได้' : 'Reachable', value: reachable, of: sent },
    { icon: Eye, label: th ? 'เข้าชมร้าน' : 'Visited', value: visited, of: reachable },
    { icon: ShoppingBag, label: th ? 'สั่งซื้อ' : 'Ordered', value: ordered, of: visited },
  ]

  return (
    <Card className="rounded-[8px] border border-black/[0.1]">
      <CardContent className="space-y-4 p-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {steps.map((s) => (
            <div key={s.label} className="rounded-xl bg-gray-50 px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-gray-500">
                <s.icon className="h-3.5 w-3.5" />
                <span className="text-[11px]">{s.label}</span>
              </div>
              <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                {s.value == null ? '—' : s.value.toLocaleString('en-US')}
              </p>
              {s.of != null && s.value != null && (
                <p className="text-[11px] text-gray-400">
                  {pct(s.value, s.of)} {th ? 'ของขั้นก่อน' : 'of prev'}
                </p>
              )}
            </div>
          ))}
        </div>

        {rec && rec.checked > 0 && (() => {
          const reached = rec.recipients.filter((r) => r.blocked === false).length
          const denom = rec.checked || 1
          const gp = Math.round((reached / denom) * 100)
          const bp = Math.round((blocked / denom) * 100)
          return (
            <div>
              <div className="flex h-2 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full bg-emerald-500/80" style={{ width: `${gp}%` }} />
                <div className="h-full bg-red-400/80" style={{ width: `${bp}%` }} />
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-emerald-500/80" />
                  {th ? 'ติดต่อได้' : 'Reachable'} {reached.toLocaleString('en-US')} ({gp}%)
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-red-400/80" />
                  {th ? 'บล็อก' : 'Blocked'} {blocked.toLocaleString('en-US')} ({bp}%)
                </span>
                {rec.capped && (
                  <span className="text-gray-400">
                    {th ? `ตรวจ ${rec.checked.toLocaleString('en-US')}/${rec.total.toLocaleString('en-US')} คน` : `checked ${rec.checked}/${rec.total}`}
                  </span>
                )}
              </div>
            </div>
          )
        })()}

        <div className="flex flex-wrap items-center gap-2">
          {visitsAvail && (
            <Badge className="border-0 bg-gray-100 text-gray-700">
              {th ? 'อัตราเข้าชม' : 'Visit rate'} {pct(visited!, reachable)}
            </Badge>
          )}
          {visited != null && ordered != null && visited > 0 && (
            <Badge className="border-0 bg-blue-100 text-blue-700">CVR {pct(ordered, visited)}</Badge>
          )}
          {!hasLink && (
            <span className="text-[11px] text-gray-400">
              {th ? 'ส่งนี้ไม่มีลิงก์ — วัดยอดเข้าชม/สั่งซื้อไม่ได้' : 'No link in this send — visits/orders not measurable'}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Coupon performance — ties the broadcast's coupon code to real Sales Orders
 * (redemptions + revenue + AOV) since the send. Company-scoped server-side.
 */
function CouponPerformance({ code, since, th }: { code: string; since: string | null; th: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: ['campaign-coupon-stats', code, since],
    queryFn: () =>
      getERPClient().call<{ redemptions: number; revenue: number; aov: number }>(
        'zaviago_backend.api.coupon_analytics.campaign_coupon_stats',
        { coupon_code: code, since },
      ),
    staleTime: 60_000,
  })
  const money = (n: number) => `฿${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`

  return (
    <Card className="rounded-[8px] border border-black/[0.1]">
      <CardContent className="space-y-3 p-6">
        <div className="flex items-center gap-2">
          <Ticket className="h-4 w-4 text-gray-500" />
          <span className="font-medium text-gray-900">{th ? 'ผลจากคูปอง' : 'Coupon performance'}</span>
          <Badge className="border-0 bg-gray-100 font-mono text-gray-700">{code}</Badge>
        </div>
        {isLoading ? (
          <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {th ? 'กำลังคำนวณ…' : 'Calculating…'}
          </div>
        ) : !data || data.redemptions === 0 ? (
          <p className="text-sm text-muted-foreground">
            {th ? 'ยังไม่มีการใช้คูปองนี้หลังการส่ง' : 'No redemptions from this send yet'}
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-gray-50 px-3 py-2.5">
              <p className="text-lg font-semibold tabular-nums text-gray-900">{data.redemptions.toLocaleString('en-US')}</p>
              <p className="text-[11px] text-gray-500">{th ? 'ใช้คูปอง' : 'Redemptions'}</p>
            </div>
            <div className="rounded-xl bg-emerald-50 px-3 py-2.5">
              <p className="text-lg font-semibold tabular-nums text-emerald-700">{money(data.revenue)}</p>
              <p className="text-[11px] text-emerald-700/70">{th ? 'ยอดขาย' : 'Revenue'}</p>
            </div>
            <div className="rounded-xl bg-gray-50 px-3 py-2.5">
              <p className="text-lg font-semibold tabular-nums text-gray-900">{money(data.aov)}</p>
              <p className="text-[11px] text-gray-500">{th ? 'เฉลี่ย/ออเดอร์' : 'AOV'}</p>
            </div>
          </div>
        )}
        <p className="text-[11px] text-gray-400">
          {th ? 'นับจากคำสั่งซื้อจริงที่ใช้คูปองนี้ ตั้งแต่วันที่ส่ง' : 'From real orders using this code since the send date'}
        </p>
      </CardContent>
    </Card>
  )
}

export function LineCampaignSentDetailPage() {
  const t = usePageTranslation({ en: enT, th: thT })
  const { language } = useLanguage()
  const th = language === 'th'
  const navigate = useNavigate()
  const { name: rawName } = useParams<{ name: string }>()
  const logName = rawName ? decodeURIComponent(rawName) : ''

  const { data, isLoading } = useLineCampaignHistory(true)
  const row = (data?.history ?? []).find((r) => r.name === logName)
  const hasLink = row?.message_type === 'campaign_link'
  // Prefer the code stored on the send; fall back to text-parse for pre-feature rows.
  const couponCode = row?.coupon_code ?? extractCouponCode(row?.content_preview)

  return (
    <PageContainer>
      <button
        type="button"
        onClick={() => navigate('/line-oa/campaigns')}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
      >
        <ArrowLeft className="h-4 w-4" />
        {t.historyTitle}
      </button>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {/* Send summary */}
          <Card className="rounded-[8px] border border-black/[0.1]">
            <CardContent className="space-y-3 p-6">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                <span>{formatSentAt(row?.sent_at ?? null)}</span>
                <span className="text-gray-300">·</span>
                <span>
                  {(row?.recipient_count ?? 0).toLocaleString('en-US')} {t.historyPeople}
                </span>
                <span className="text-gray-300">·</span>
                {row?.status === 'sent' ? (
                  <Badge className="bg-green-100 text-green-700 border-0">{t.historyStatusSent}</Badge>
                ) : row ? (
                  <Badge className="bg-red-100 text-red-700 border-0">{t.historyStatusFailed}</Badge>
                ) : null}
              </div>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-900">
                {row?.content_preview || <span className="text-gray-400">—</span>}
              </p>
            </CardContent>
          </Card>

          {/* Performance funnel — Sent → Reachable → Visited → Ordered + rates */}
          <CampaignFunnel logName={logName} sent={row?.recipient_count ?? 0} hasLink={hasLink} th={th} />

          {/* Coupon performance — redemptions + revenue for the code in the message */}
          {couponCode && <CouponPerformance code={couponCode} since={row?.sent_at ?? null} th={th} />}

          {/* Paginated recipient list */}
          <Card className="rounded-[8px] border border-black/[0.1] overflow-hidden">
            <CardContent className="p-0">
              <RecipientList logName={logName} t={t} />
            </CardContent>
          </Card>
        </div>
      )}
    </PageContainer>
  )
}
